import { inject, Injectable, signal, type Signal } from '@angular/core';
import { Router } from '@angular/router';
import { nanoid } from 'nanoid';
import { DatabaseStorageService } from './database-storage.service';
import { LoggerService } from './logger.service';
import { TranslationService } from './translation.service';
import { LOCAL_STORAGE, UserStorageService } from './user-storage.service';

/**
 * Dynamic imports for SQLite implementations
 *
 * TODO: Future improvement - create a DatabaseAdapter wrapper class to centralize
 * type handling and provide a consistent typed interface. This would eliminate
 * scattered 'as' type assertions throughout the codebase and provide a single
 * place to manage the worker protocol types.
 */
type DB = any; // DatabaseWorkerProxy (talks to database.worker.ts)
type SQLite3 = any;

/**
 * Local SQLite database service using @sqlite.org/sqlite-wasm in a Web Worker
 * This is the PRIMARY source of truth for all data in the application
 * All CRUD operations happen here - no backend API calls needed
 * Persists to OPFS via the SAH Pool VFS (see database.worker.ts)
 *
 * DEVELOPMENT TESTING: Artificial Delay
 * To enable artificial delays for testing loading states, spinners, and race conditions:
 * 1. Open browser DevTools window.console
 * 2. Set localStorage values using JSON serialization:
 *    localStorage.setItem('data-delay-enabled', 'true');  // Note: boolean as JSON
 *    localStorage.setItem('data-delay-min-ms', '500');    // minimum delay in ms
 *    localStorage.setItem('data-delay-max-ms', '2000');   // maximum delay in ms
 * 3. Reload the page
 * 4. All database operations will now include a random delay between min and max
 * 5. To disable: localStorage.setItem('data-delay-enabled', 'false'); and reload
 *
 * Note: Code uses getGlobalTypedItem<boolean>() which parses JSON, so 'true'/'false' strings work.
 */
/**
 * Query signal wrapper with data and loading state
 */
export interface QuerySignal<T> {
  data: Signal<T>;
  loading: Signal<boolean>;
  error: Signal<string | null>;
}

/**
 * Query refresh function registered for a table
 */
type QueryRefreshFn = () => Promise<void>;

/**
 * Proxy for communicating with the database Web Worker.
 * Sends { id, type, ... } messages and resolves/rejects Promises
 * when the Worker posts back { id, result?, error? }.
 */
class DatabaseWorkerProxy {
  private worker: Worker;
  private pending = new Map<string, { resolve: (v: any) => void; reject: (e: Error) => void }>();
  private nextId = 0;
  private onError: (...args: any[]) => void;

  constructor(worker: Worker, onError: (...args: any[]) => void) {
    this.worker = worker;
    this.onError = onError;
    this.worker.onmessage = (event: MessageEvent) => {
      const { id, result, error } = event.data;
      const req = this.pending.get(id);
      if (req) {
        this.pending.delete(id);
        if (error) {
          req.reject(new Error(error));
        } else {
          req.resolve(result);
        }
      }
    };
    this.worker.onerror = (event: ErrorEvent) => {
      this.onError('[DB Worker] Unhandled worker error:', event.message);
    };
  }

  send(type: string, payload?: Record<string, any>): Promise<any> {
    return new Promise((resolve, reject) => {
      const id = String(++this.nextId);
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage({ id, type, ...payload });
    });
  }

  terminate(): void {
    this.worker.terminate();
    for (const [, req] of this.pending) {
      req.reject(new Error('Worker terminated'));
    }
    this.pending.clear();
  }
}

@Injectable({
  providedIn: 'root',
})
/**
 * Local SQLite database service (offline-first, via @sqlite.org/sqlite-wasm).
 *
 * ## Public API — use these in feature services:
 *
 * | Method               | Purpose                                              |
 * |----------------------|------------------------------------------------------|
 * | `exec(sql, params?)` | SELECT queries → returns `any[]` row objects          |
 * | `run(sql, params?)`  | INSERT / UPDATE / DELETE → returns nothing            |
 * | `get(sql, params?)`  | SELECT single row → returns first row or `null`       |
 * | `createQuery(table, fn)` | Reactive query → returns `QuerySignal<T>`         |
 * | `notifyDataChange(table)` | Triggers refresh of reactive queries for a table |
 * | `generateId()`       | Generates a nanoid primary key                       |
 * | `init()`             | Ensures DB is initialized (called automatically)     |
 *
 * ## Critical rules:
 *
 * 1. **Always `await`** every `exec()`, `run()`, `get()` call.
 *    Calls are serialized through a mutex that transactions rely on;
 *    unawaited calls interleave with other work and lose their errors.
 *
 * 2. **Always use parameterized queries** (`?` placeholders + params array).
 *    Never interpolate values into SQL strings.
 *
 * 3. **Never access `this.db` (the raw adapter) directly** from feature services.
 *    The public methods serialize operations through a WASM mutex.
 *    Direct adapter access bypasses the mutex and causes race conditions.
 *
 * 4. **Use `createQuery()` for reactive data** in components, not manual polling.
 *    Call `notifyDataChange()` after writes to refresh dependent queries.
 */
export class DatabaseService {
  private storage = inject(DatabaseStorageService);
  private localStorage = inject(LOCAL_STORAGE);

  private translateService = inject(TranslationService);
  // Shared global instance — Worker hosts the actual sqlite-wasm runtime
  private static globalDb: DB | null = null;
  private static globalWorker: Worker | null = null;
  private static globalWorkerProxy: DatabaseWorkerProxy | null = null;
  private static initializationCount = 0;

  private db: DB | null = null;
  private initPromise: Promise<void> | null = null;
  private siteId: string | null = null;

  // Initialization state tracking to prevent redundant attempts after permanent failures
  private initState: 'uninitialized' | 'initializing' | 'initialized' | 'failed' | 'paused' =
    'uninitialized';

  /** Last initialization error - publicly accessible for error page diagnostics */
  public initError: Error | null = null; // Store the first failure error

  /**
   * Check if database is in failed state (public accessor for guards)
   */
  get isFailed(): boolean {
    return this.initState === 'failed';
  }

  /**
   * Check if database is paused (non-leader tab, waiting for leadership)
   */
  get isPaused(): boolean {
    return this.initState === 'paused';
  }

  /**
   * Check if verbose database logging is enabled
   * Enable via: localStorage.setItem('debug-database-verbose', 'true')
   * Disable via: localStorage.removeItem('debug-database-verbose')
   */
  private get isVerboseLoggingEnabled(): boolean {
    return this.localStorage.getItem('debug-database-verbose') === 'true';
  }

  // Query registry for automatic refresh on data changes
  private queryRegistry = new Map<string, Set<QueryRefreshFn>>();

  // Logger for database operations
  private log = inject(LoggerService).getLogger('database');

  // Inject Router for navigation to error pages
  private router = inject(Router);

  /**
   * Get database name based on customer_id from JWT
   * Multi-tenant: Each organization has its own isolated database
   *
   * Database naming: `cp2-customer_${customerId}`
   *
   * NOTE: Cannot use AuthService here due to circular dependency
   * (AuthService → DatabaseService → getDbName → AuthService)
   * Instead, read token directly from localStorage
   */
  private getDbName(): string {
    this.log.debug('Getting database name...');

    try {
      // Read token from localStorage
      const token = this.localStorage.getItem('auth_token');
      this.log.debug('Token retrieved:', token ? `${token.substring(0, 20)}...` : 'null');

      if (token) {
        // Decode JWT manually (same logic as AuthService.decodeJwt)
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join(''),
        );
        const payload = JSON.parse(jsonPayload);
        this.log.debug('JWT decoded, customer_id:', payload.customer_id);

        // Check if customer_id is missing from JWT (malformed token)
        if (!payload.hasOwnProperty('customer_id')) {
          this.log.debug('JWT payload missing customer_id field:', payload);
          throw new Error('Invalid token: customer_id missing from JWT payload');
        }

        // Check if user has no organization assigned (customer_id is null)
        if (payload.customer_id === null) {
          this.log.info('User has no organization assigned - database unavailable');
          throw new Error('User not assigned to organization');
        }

        // Valid customer_id found
        const dbName = `cp2-customer_${payload.customer_id}`;
        this.log.info('🔑 Using database:', dbName, 'for customer_id:', payload.customer_id);
        return dbName;
      } else {
        this.log.debug('No auth_token found - database initialization not possible');
        throw new Error('Authentication required: No auth token found');
      }
    } catch (error) {
      // Only log as error if it's not an expected authentication error
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (
        errorMessage.includes('Authentication required') ||
        errorMessage.includes('customer_id missing') ||
        errorMessage.includes('User not assigned to organization')
      ) {
        this.log.debug('Cannot get database name:', errorMessage);
      } else {
        this.log.error('Failed to get customer_id from JWT:', errorMessage);
      }
      throw error instanceof Error ? error : new Error('Failed to decode authentication token');
    }
  }

  private userStorage = inject(UserStorageService);

  /**
   * Check if error indicates authentication required (not a fatal DB error)
   */
  private isAuthenticationError(error: Error): boolean {
    return (
      error.message.includes('Authentication required') ||
      error.message.includes('auth_token') ||
      error.message.includes('authentication token') ||
      error.message.includes('customer_id') ||
      error.message.includes('JWT payload') ||
      error.message.includes('User not assigned to organization')
    );
  }

  /**
   * Check if error is a transient OPFS SAH pool lock conflict.
   * This happens when two tabs simultaneously try to acquire the OPFS SAH pool —
   * e.g. after a leadership handoff where the old leader's worker hasn't fully
   * terminated before the new leader's worker starts.
   * This is NOT a permanent failure; the correct response is to allow init() to retry.
   */
  private isOpfsLockError(error: Error): boolean {
    const msg = error.message;
    return (
      msg.includes('createSyncAccessHandle') ||
      msg.includes('Access Handle') ||
      msg.includes('NoModificationAllowedError')
    );
  }

  /**
   * Check if error is a fatal database corruption/access error
   * These errors require database reset/reload - cannot be recovered
   */
  private isFatalDatabaseError(error: Error): boolean {
    const fatalPatterns = [
      'unable to open database file',
      'memory access out of bounds',
      'file not found:',
      'Cannot read properties of undefined',
      'database disk image is malformed',
      'database is locked', // Can be fatal if persistent
      'SQLITE_IOERR',
      'SQLITE_CORRUPT',
    ];

    return fatalPatterns.some((pattern) => error.message.includes(pattern));
  }

  constructor() {
    // Constructor should ONLY set up data structures, no I/O or side effects
    DatabaseService.initializationCount++;
    this.log.debug(
      `DatabaseService instance created (count: ${DatabaseService.initializationCount})`,
    );
  }

  /**
   * Initialize site ID for CRDT tracking
   * Called lazily on first database init, not in constructor
   */
  private initializeSiteId(): void {
    if (this.siteId) return; // Already initialized

    // Generate or restore site ID for CRDT (global device setting)
    this.siteId = this.userStorage.getGlobalItem('crsql-site-id');
    if (!this.siteId) {
      this.siteId = nanoid();
      this.userStorage.setGlobalItem('crsql-site-id', this.siteId);
    }
  }

  /**
   * Log memory statistics for diagnostics
   */
  private logMemoryStats(context: string): void {
    // Check if performance.memory is available (Chrome/Edge only)
    if ('memory' in performance) {
      const mem = (performance as any).memory;
      const usedMB = mem.usedJSHeapSize / 1024 / 1024;
      const totalMB = mem.totalJSHeapSize / 1024 / 1024;
      const limitMB = mem.jsHeapSizeLimit / 1024 / 1024;
      const availableMB = (mem.jsHeapSizeLimit - mem.usedJSHeapSize) / 1024 / 1024;
      const usedPercent = (mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100;

      this.log.debug(`MEMORY ${context}:`, {
        usedJSHeap: `${usedMB.toFixed(2)} MB`,
        totalJSHeap: `${totalMB.toFixed(2)} MB`,
        heapLimit: `${limitMB.toFixed(2)} MB`,
        available: `${availableMB.toFixed(2)} MB`,
        usedPercent: `${usedPercent.toFixed(1)}%`,
      });

      // wa-sqlite WASM needs significant runtime memory:
      // - SQLite engine allocates page cache (default 2000 pages × 4KB = 8MB)
      // - Emscripten runtime overhead (~20-50MB)
      // - WASM linear memory is pre-allocated in 64KB pages
      // TOTAL ESTIMATED: 256-512MB depending on usage
      const ESTIMATED_WASM_NEED_MB = 512;

      if (availableMB < ESTIMATED_WASM_NEED_MB) {
        this.log.warn(`⚠️ LOW MEMORY WARNING`);
        this.log.warn(`Available: ${availableMB.toFixed(2)} MB`);
        this.log.warn(`Estimated need: ${ESTIMATED_WASM_NEED_MB} MB`);
        this.log.warn(`Shortfall: ${(ESTIMATED_WASM_NEED_MB - availableMB).toFixed(2)} MB`);
      } else {
        this.log.debug(
          `✅ Sufficient for WASM (need ~${ESTIMATED_WASM_NEED_MB}MB, have ${availableMB.toFixed(2)}MB)`,
        );
      }
    } else {
      this.log.debug(
        `MEMORY ${context}: performance.memory not available (use Chrome/Edge with --enable-precise-memory-info flag)`,
      );
    }
  }

  /**
   * Development helper: Add artificial delay to database operations
   * Configure via localStorage:
   * - data-delay-enabled: 'true' | 'false'
   * - data-delay-min-ms: number (default 500)
   * - data-delay-max-ms: number (default 2000)
   */
  private async artificialDelay(): Promise<void> {
    const enabled = this.userStorage.getGlobalTypedItem<boolean>('data-delay-enabled') ?? false;
    if (!enabled) {
      return; // No async overhead when disabled
    }

    const min = Number(this.userStorage.getGlobalItem('data-delay-min-ms')) || 500;
    const max = Number(this.userStorage.getGlobalItem('data-delay-max-ms')) || 2000;
    const delay = Math.floor(Math.random() * (max - min + 1)) + min;

    await new Promise((resolve) => setTimeout(resolve, delay));
  }

  /**
   * Initialize the SQL.js library and database
   * Creates the database schema if it doesn't exist
   */
  async init(): Promise<void> {
    // If paused (non-leader tab), do not auto-initialize — waiting for leadership
    if (this.initState === 'paused') {
      this.log.debug('[DB INIT] ⏸ Database is paused - waiting for leadership, skipping init');
      return;
    }

    // If initialization failed permanently (not due to auth), throw the stored error immediately
    // This prevents redundant init attempts and error spam
    if (
      this.initState === 'failed' &&
      this.initError &&
      !this.isAuthenticationError(this.initError)
    ) {
      this.log.debug(
        '[DB INIT] ⚠️ Database initialization previously failed - throwing stored error',
      );
      throw this.initError;
    }

    // Return existing promise if initialization is in progress
    if (this.initPromise) {
      if (this.isVerboseLoggingEnabled) {
        this.log.debug('[DB INIT] ⏳ Initialization in progress, waiting for existing promise...');
      }
      this.log.debug('[DB INIT] Initialization already in progress, waiting...');
      return this.initPromise;
    }

    // Return immediately if already initialized
    if (this.db && this.initState === 'initialized') {
      if (this.isVerboseLoggingEnabled) {
        this.log.debug('[DB INIT] ✅ Already initialized (this.db exists, state=initialized)');
      }
      // this.log.debug('[DB INIT] Database already initialized, skipping');
      return Promise.resolve();
    }

    if (this.isVerboseLoggingEnabled) {
      this.log.debug(
        `[DB INIT] 🚀 Starting initialization (current state: ${this.initState}, this.db exists: ${!!this.db})`,
      );
    }

    // Initialize site ID for CRDT (must happen before database initialization)
    this.initializeSiteId();

    // Mark as initializing
    this.initState = 'initializing';

    // Create initialization promise
    this.initPromise = this.initializeDatabase();

    try {
      await this.initPromise;
      if (this.isVerboseLoggingEnabled) {
        this.log.debug('[DB INIT] ✅ initializeDatabase() completed successfully');
      }
      this.initState = 'initialized';
      this.initError = null; // Clear any previous errors
      if (this.isVerboseLoggingEnabled) {
        this.log.debug('[DB INIT] 🎯 State set to "initialized"');
      }
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));

      // Check if this is a fatal database error
      if (this.isFatalDatabaseError(errorObj)) {
        this.initState = 'failed';
        this.initError = errorObj;
        this.initPromise = null; // Clear promise to allow retry after recovery
        this.log.error(
          '[DB INIT] 💀 FATAL DATABASE ERROR - database initialization failed:',
          errorObj.message,
        );
        this.log.error('[DB INIT] 💀 FATAL ERROR:', errorObj);

        // For "database disk image is malformed" on fresh initialization,
        // this indicates a problem with database CREATION, not corruption.
        // The deleteCorruptedDatabase() method can be called manually if needed.

        throw errorObj; // Rethrow to fail fast
      }

      // Check if this is an authentication error
      if (this.isAuthenticationError(errorObj)) {
        // For auth errors, don't mark as permanently failed - allow retry when token becomes available
        this.initState = 'uninitialized';
        this.initError = null;
        this.log.debug(
          '[DB INIT] ⚠️ Authentication required - database init can be retried when token becomes available',
        );
      } else if (this.isOpfsLockError(errorObj)) {
        // OPFS SAH pool lock conflict means another tab currently holds the pool,
        // which makes this tab definitionally a non-leader. Set state to 'paused'
        // so the guard/resolvers treat it exactly like a known non-leader tab:
        // they skip DB work, the overlay appears, and the tab waits until it wins
        // the Web Lock and resumeAfterLeadership() is called.
        // NOTE: the worker never fully opened (it failed on createSyncAccessHandle),
        // so there is nothing to close — setting initState directly is safe here.
        this.initState = 'paused';
        this.initError = null;
        this.log.warn(
          '[DB INIT] ⚠️ OPFS SAH pool lock conflict — another tab holds the pool. ' +
            'Entering paused state; will resume when this tab wins leadership.',
        );
      } else {
        // For other errors, mark as permanently failed
        this.initState = 'failed';
        this.initError = errorObj;
        this.log.error(
          '[DB INIT] ❌ Initialization failed permanently - future init() calls will throw immediately',
        );
      }
      throw errorObj;
    } finally {
      this.initPromise = null;
    }
  }

  /**
   * Delete the local database and reset to allow fresh initialization
   * Used for:
   * - Database corruption recovery
   * - Manual database reset (testing/troubleshooting)
   * - Fresh sync after backend schema changes
   */
  async deleteCorruptedDatabase(): Promise<void> {
    this.log.warn('[DB RESET] 🗑️ Deleting local database...');

    try {
      // Close existing connection if any
      if (this.db) {
        try {
          await this.db.close();
        } catch {
          // Ignore close errors for corrupted databases
        }
      }

      // Get database path before clearing state
      const dbPath = this.getDbName();

      this.log.info(`[DB RESET] Deleting database: ${dbPath}`);

      // Clear all state
      this.db = null;
      this.initPromise = null;
      this.initState = 'uninitialized';
      this.initError = null;
      DatabaseService.globalDb = null;
      this.queryRegistry.clear();

      // Terminate Worker if running
      if (DatabaseService.globalWorkerProxy) {
        DatabaseService.globalWorkerProxy.terminate();
        DatabaseService.globalWorkerProxy = null;
      }
      DatabaseService.globalWorker = null;

      // Delete database from IndexedDB
      await this.deletePersistedDatabase(dbPath, '[DB RESET]');

      this.log.info('[DB RESET] ✅ Database deleted - ready for fresh initialization');
    } catch (error) {
      this.log.error('[DB RESET] ❌ Error during database deletion:', error);
      throw error;
    }
  }

  /**
   * Close the database connection and reset global state
   * CRITICAL: Call this on logout to prevent database reuse across different users
   */
  async closeDatabase(): Promise<void> {
    this.log.debug('[DB CLOSE] Closing database connection...');
    const openDbPath: string | undefined = this.db?.filename;

    try {
      // Close the database connection if it exists
      if (this.db) {
        this.log.debug('[DB CLOSE] Closing database instance...');
        await this.db.close();
        this.log.debug('[DB CLOSE] ✅ Database instance closed');
      }

      // Clear instance variables
      this.db = null;
      this.initPromise = null;
      this.initState = 'uninitialized'; // Reset init state
      this.initError = null; // Clear any stored error

      // Clear global static instances to force re-initialization on next login
      DatabaseService.globalDb = null;
      if (DatabaseService.globalWorkerProxy) {
        DatabaseService.globalWorkerProxy.terminate();
        DatabaseService.globalWorkerProxy = null;
      }
      DatabaseService.globalWorker = null;
      this.log.debug('[DB CLOSE] ✅ Global instances cleared');

      // Clear query registry to prevent stale query refreshes
      this.queryRegistry.clear();

      // Logout deliberately deletes local data so it can't carry over to the next
      // user on a shared device. (The worker's 'close' only releases file handles;
      // until 2026-10 it also deleted the pool via removeVfs(), which made this
      // happen implicitly — and wiped data on every multi-tab leadership handoff.)
      if (openDbPath) {
        await this.deletePersistedDatabase(openDbPath, '[DB CLOSE]');
      }
      this.log.debug('[DB CLOSE] ✅ Query registry cleared');

      this.log.info('[DB CLOSE] 🔒 Database closed successfully - ready for new user login');
    } catch (error) {
      this.log.error('[DB CLOSE] ❌ Error closing database:', error);
      // Even if close fails, still reset state to force re-initialization
      this.db = null;
      this.initPromise = null;
      this.initState = 'uninitialized'; // Reset init state
      this.initError = null; // Clear any stored error
      DatabaseService.globalDb = null;
      if (DatabaseService.globalWorkerProxy) {
        DatabaseService.globalWorkerProxy.terminate();
        DatabaseService.globalWorkerProxy = null;
      }
      DatabaseService.globalWorker = null;
      this.queryRegistry.clear();
    }
  }

  private async initializeDatabase(): Promise<void> {
    try {
      this.log.debug('[DB INIT] Starting database initialization...');
      this.log.debug('[DB INIT] Database name:', this.getDbName());

      // Check if global instance already exists AND is for the same database
      // CRITICAL: Must verify database name matches to prevent user A from seeing user B's data
      const requestedDbName = this.getDbName();
      const currentDbPath = DatabaseService.globalDb?.filename;
      const expectedDbPath = requestedDbName; // Already includes prefix (cp2-)

      if (
        DatabaseService.globalDb &&
        DatabaseService.globalWorker &&
        currentDbPath === expectedDbPath
      ) {
        this.log.debug('[DB INIT] ♻️  Reusing existing Worker database for:', requestedDbName);
        this.db = DatabaseService.globalDb;
        this.log.debug('[DB INIT] ✅ Reused successfully');
        this.logMemoryStats('after-reuse');

        return;
      } else if (DatabaseService.globalDb && currentDbPath !== expectedDbPath) {
        this.log.warn(
          `[DB INIT] ⚠️  Database mismatch detected! Current: ${currentDbPath}, Expected: ${expectedDbPath}`,
        );
        this.log.warn('[DB INIT] 🔄 Closing old database and opening new one...');
        await DatabaseService.globalDb.close();
        DatabaseService.globalDb = null;
        if (DatabaseService.globalWorkerProxy) {
          DatabaseService.globalWorkerProxy.terminate();
          DatabaseService.globalWorkerProxy = null;
        }
        DatabaseService.globalWorker = null;
      }

      this.logMemoryStats('before-wasm-load');

      // Spawn Web Worker to host the sqlite-wasm runtime off the main thread.
      // This prevents Safari's renderer watchdog from killing the page due to
      // prolonged WASM execution (the root cause of the Asyncify crash).
      this.log.debug('[DB INIT] Spawning database Worker...');
      this.log.debug('[DB INIT] Browser:', window.navigator.userAgent);

      let worker: Worker;
      let proxy: DatabaseWorkerProxy;

      try {
        worker = DatabaseService.spawnWorker();
        proxy = new DatabaseWorkerProxy(worker, this.log.error.bind(this.log));
        this.log.debug('[DB INIT] ✅ Worker spawned');
      } catch (workerError: any) {
        this.log.error('[DB INIT] ❌ CRITICAL: Worker spawn failed');
        this.log.error('[DB INIT] Error details:', workerError);
        throw new Error(`CRITICAL: Failed to spawn database Worker. Error: ${workerError.message}`);
      }

      this.logMemoryStats('after-worker-spawn');

      // Open or create database inside the Worker (sqlite-wasm + OPFS SAH Pool persistence)
      const dbPath = this.getDbName();
      this.log.debug('[DB INIT] Opening database in Worker with path:', dbPath);

      // Retry logic for IndexedDB connection issues
      // When user clears client data, IndexedDB connections take time to fully close
      // Retry up to 3 times with exponential backoff
      let retryCount = 0;
      const maxRetries = 3;
      let lastError: Error | null = null;

      while (retryCount <= maxRetries) {
        try {
          const openResult = await proxy.send('open', { dbPath });
          this.log.debug(
            `[DB INIT] ✅ Worker opened database (reused: ${openResult?.reused ?? false})`,
          );

          // Create proxy adapter matching the db API shape used throughout the app
          this.db = {
            filename: dbPath,
            exec: async (sql: string) => {
              return proxy.send('exec', { sql });
            },
            execO: async (sql: string, params?: any[]) => {
              return proxy.send('execO', { sql, params });
            },
            onUpdate: (_callback: any) => {
              // No-op for custom sync (update hooks not used)
            },
            close: async () => {
              await proxy.send('close');
              proxy.terminate();
            },
          } as any;

          break; // Success - exit retry loop
        } catch (error) {
          lastError = error as Error;
          const errorMsg = (error as Error)?.message ?? '';
          const isIdbClosingError =
            errorMsg.includes('database connection is closing') ||
            errorMsg.includes('InvalidStateError');

          if (isIdbClosingError && retryCount < maxRetries) {
            const delay = Math.pow(2, retryCount) * 500; // 500ms, 1s, 2s
            this.log.warn(
              `[DB INIT] ⚠️ IndexedDB connection closing (attempt ${retryCount + 1}/${maxRetries + 1}), retrying in ${delay}ms...`,
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            retryCount++;
          } else {
            // Not a retryable error, or max retries exceeded
            proxy.terminate();
            throw error;
          }
        }
      }

      if (!this.db) {
        proxy.terminate();
        throw new Error(
          `Failed to open database after ${maxRetries + 1} attempts. Last error: ${lastError?.message}`,
        );
      }

      this.log.debug(`[DB INIT] ✅ Database opened successfully (${retryCount} retries)`);
      this.log.debug('[DB INIT] Site ID:', this.siteId);

      this.logMemoryStats('after-db-open');

      // Store as global instance
      DatabaseService.globalDb = this.db;
      DatabaseService.globalWorker = worker;
      DatabaseService.globalWorkerProxy = proxy;
      this.log.debug('[DB INIT] 💾 Stored as global instance for reuse');

      // Custom sync mode: Schema managed by backend via sync
      this.log.debug('[DB INIT] Custom sync mode - schema managed by backend via sync');
      this.log.debug('[DB INIT] ✅ Database ready for custom sync');
      this.log.debug('Database initialized successfully');
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      // Only log as error if it's not an expected authentication error
      if (this.isAuthenticationError(err)) {
        this.log.debug('Database initialization requires authentication:', err.message);
      } else {
        this.log.error('Failed to initialize database:', err.message);
      }
      throw error;
    }
  }

  /**
   * Note: Custom sync mode gets schema from backend via sync.
   * Schema is NOT created client-side.
   */

  /**
   * Execute a SELECT query and return results as an array of row objects.
   *
   * This is the primary method for reading data from the database.
   * All operations are serialized through a WASM mutex to prevent concurrent
   * access errors (wa-sqlite's Asyncify is not reentrant).
   *
   * @param sql - SQL SELECT statement. Use `?` placeholders for parameters.
   * @param params - Optional array of parameter values bound to `?` placeholders.
   * @returns Array of row objects with column names as keys.
   *
   * @example
   * // Simple query
   * const rows = await this.db.exec('SELECT * FROM assets');
   *
   * // Parameterized query (ALWAYS use params for user input)
   * const rows = await this.db.exec(
   *   'SELECT * FROM assets WHERE id = ?',
   *   [checkPointId]
   * );
   *
   * @remarks
   * **IMPORTANT**: Always `await` this method. Never fire-and-forget.
   * **IMPORTANT**: Always use parameterized queries (`?` placeholders) — never
   * interpolate values into SQL strings.
   */
  async exec(sql: string, params?: any[]): Promise<any[]> {
    const execId = Math.random().toString(36).substring(7);

    if (this.isVerboseLoggingEnabled) {
      const sqlPreview = sql.length > 100 ? sql.substring(0, 100) + '...' : sql;
      this.log.debug(
        `[DB EXEC ${execId}] 🔵 START - SQL: ${sqlPreview}`,
        params ? `Params: ${JSON.stringify(params)}` : 'No params',
      );
    }

    // CRITICAL: Fail immediately if database is in failed state
    if (this.initState === 'failed' && this.initError) {
      if (this.isVerboseLoggingEnabled) {
        this.log.error(`[DB EXEC ${execId}] ❌ Database is in FAILED state, rejecting call`);
      }
      throw this.initError;
    }

    // Automatically ensure database is initialized
    if (this.isVerboseLoggingEnabled) {
      this.log.debug(`[DB EXEC ${execId}] 📞 Calling init()...`);
    }
    try {
      await this.init();
      if (this.isVerboseLoggingEnabled) {
        this.log.debug(`[DB EXEC ${execId}] ✅ init() returned`);
      }
    } catch (error) {
      // Check if fatal error
      if (error instanceof Error && this.isFatalDatabaseError(error)) {
        if (this.isVerboseLoggingEnabled) {
          this.log.error(`[DB EXEC ${execId}] 💀 FATAL init error, failing immediately`);
        }
        throw error;
      }
      throw error;
    }

    if (!this.db) {
      if (this.isVerboseLoggingEnabled) {
        this.log.debug(`[DB EXEC ${execId}] ❌ Database not initialized!`);
      }
      throw new Error('Database not initialized. Call init() first.');
    }

    await this.artificialDelay();

    // wa-sqlite execO returns array of row objects (like sql.js getAsObject)
    // CRITICAL: Must await execO - it returns a Promise!
    if (this.isVerboseLoggingEnabled) {
      this.log.debug(`[DB EXEC ${execId}] 🔨 Calling execO()...`);
    }
    try {
      const results = await this.db.execO(sql, params);
      if (this.isVerboseLoggingEnabled) {
        this.log.debug(`[DB EXEC ${execId}] ✅ execO() returned ${results?.length || 0} rows`);
      }
      return results;
    } catch (error) {
      // Check for fatal database errors
      if (error instanceof Error && this.isFatalDatabaseError(error)) {
        this.log.error(`[DB EXEC ${execId}] 💀 FATAL execO error:`, error.message);
        this.initState = 'failed';
        this.initError = error;
        this.initPromise = null;
      }
      throw error;
    }
  }

  /**
   * Execute a write statement (INSERT, UPDATE, DELETE) without returning rows.
   *
   * This is the primary method for modifying data in the database.
   * All operations are serialized through a WASM mutex to prevent concurrent
   * access errors (wa-sqlite's Asyncify is not reentrant).
   *
   * @param sql - SQL write statement. Use `?` placeholders for parameters.
   * @param params - Optional array of parameter values bound to `?` placeholders.
   *
   * @example
   * // Insert with parameters
   * await this.db.run(
   *   'INSERT INTO assets (id, data) VALUES (?, ?)',
   *   [id, JSON.stringify(data)]
   * );
   *
   * // Update with parameters
   * await this.db.run(
   *   'UPDATE assets SET data = ? WHERE id = ?',
   *   [JSON.stringify(data), id]
   * );
   *
   * @remarks
   * **IMPORTANT**: Always `await` this method. Never fire-and-forget.
   * **IMPORTANT**: Always use parameterized queries (`?` placeholders) — never
   * interpolate values into SQL strings.
   */
  async run(sql: string, params?: any[]): Promise<void> {
    const runId = Math.random().toString(36).substring(7);
    const sqlPreview = sql.length > 100 ? sql.substring(0, 100) + '...' : sql;
    this.log.debug(
      `[DB RUN ${runId}] 🔵 START - SQL: ${sqlPreview}`,
      params ? `Params: ${JSON.stringify(params)}` : 'No params',
    );

    // Automatically ensure database is initialized
    await this.init();

    if (!this.db) {
      this.log.debug(`[DB RUN ${runId}] ❌ Database not initialized!`);
      throw new Error('Database not initialized. Call init() first.');
    }

    await this.artificialDelay();

    this.log.debug(`[DB RUN ${runId}] 🔨 Executing statement...`);
    try {
      if (!params || params.length === 0) {
        // No parameters - use simple exec
        await this.db.exec(sql);
      } else {
        // For parameterized INSERT/UPDATE/DELETE, call execO but don't collect rows
        // execO will execute the statement and step through (INSERT returns SQLITE_DONE)
        await this.db.execO(sql, params);
      }
      this.log.debug(`[DB RUN ${runId}] ✅ Statement executed successfully`);

      // Track change for sync (if not a system table)
      await this.trackChangeForSync(sql, params);
    } catch (error) {
      this.log.error(`[DB RUN ${runId}] ❌ Execution failed:`, error);
      this.log.error('Database run error:', {
        sql,
        params,
        error,
      });
      throw error;
    }
  }

  /**
   * Execute a SELECT query and return the first row, or null if no results.
   *
   * Convenience wrapper around {@link exec} for queries that expect a single row.
   *
   * @param sql - SQL SELECT statement. Use `?` placeholders for parameters.
   * @param params - Optional array of parameter values bound to `?` placeholders.
   * @returns The first row as an object, or `null` if no rows match.
   *
   * @example
   * const asset = await this.db.get<{ id: string; data: string }>(
   *   'SELECT * FROM assets WHERE id = ?',
   *   [checkPointId]
   * );
   * if (asset) {
   *   // use asset.id, asset.data
   * }
   *
   * @remarks
   * **IMPORTANT**: Always `await` this method. Never fire-and-forget.
   */
  async get<T = any>(sql: string, params?: any[]): Promise<T | null> {
    // Automatically ensure database is initialized
    await this.init();

    const results = await this.exec(sql, params);
    return results.length > 0 ? (results[0] as T) : null;
  }

  /**
   * @internal Run SQL without artificial delay — **private, do not use directly**.
   *
   * Used only for schema creation and seed data during database initialization.
   * External callers must use {@link run} or {@link exec} instead.
   */
  private async runInternal(sql: string, params?: any[]): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }

    try {
      if (params && params.length > 0) {
        // Use execO for parameterized queries (routes through mutex + high-level API)
        await this.db.execO(sql, params);
      } else {
        await this.db.exec(sql);
      }
    } catch (error) {
      this.log.error('Database run error:', {
        sql,
        params,
        error,
      });
      throw error;
    }
  }

  /**
   * @internal Subscribe to low-level database change notifications.
   *
   * Used by SyncService to detect when data is synced from other clients.
   * Feature services should use {@link createQuery} for reactive data instead.
   */
  async onUpdate(
    callback: (type: number, dbName: string, tableName: string, rowid: bigint) => void,
  ): Promise<() => void> {
    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }

    // Register the callback with wa-sqlite
    // Returns an unsubscribe function
    return this.db.onUpdate(callback);
  }

  /**
   * Notify all registered reactive queries that a table's data has changed.
   *
   * Call this after modifying data (via {@link run}) to trigger automatic
   * refresh of any {@link createQuery} signals watching that table.
   * Queries refresh asynchronously through the WASM mutex.
   *
   * @param tableName - The database table name that was modified.
   *
   * @example
   * await this.db.run('INSERT INTO assets ...', [...]);
   * this.db.notifyDataChange('assets');
   */
  notifyDataChange(tableName: string): void {
    this.log.debug(`[DB] 🔔 Data changed in table: ${tableName}`);

    const queries = this.queryRegistry.get(tableName);
    if (queries && queries.size > 0) {
      this.log.debug(`[DB] 🔄 Refreshing ${queries.size} registered queries for ${tableName}`);
      queries.forEach((refresh) => {
        refresh().catch((err) => {
          this.log.error(`[DB] ❌ Query refresh failed for ${tableName}:`, err);
        });
      });
    } else {
      this.log.debug(`[DB] ℹ️ No queries registered for ${tableName}`);
    }
  }

  /**
   * Pause database for leadership transition — terminates worker and releases SAH handles.
   * Called by CustomSyncService when this tab loses or does not win the Web Lock.
   */
  async pauseForLeadership(): Promise<void> {
    if (this.initState === 'paused') {
      return; // Already paused — idempotent
    }
    this.log.info('[DB] ⏸ Pausing database for leadership transition');
    if (DatabaseService.globalWorkerProxy) {
      try {
        await DatabaseService.globalWorkerProxy.send('close', {});
      } catch (_) {}
      DatabaseService.globalWorkerProxy.terminate();
      DatabaseService.globalWorkerProxy = null;
    }
    DatabaseService.globalWorker = null;
    this.db = null;
    this.initPromise = null;
    this.initState = 'paused';
    this.initError = null;
    this.queryRegistry.clear();
  }

  /**
   * Resume after winning leadership — resets initState to 'uninitialized' so init() can run.
   * Caller (CustomSyncService.doStart) must invoke initDatabase() / init() afterwards.
   */
  async resumeAfterLeadership(): Promise<void> {
    this.log.info('[DB] ▶️ Resuming after winning leadership');
    this.initState = 'uninitialized';
    this.initError = null;
  }

  /**
   * Re-run all registered query refresh functions after leadership is gained.
   * Called from doStart() after DB re-initialization completes.
   */
  async refreshAllQueries(): Promise<void> {
    this.log.info(`[DB] 🔄 Refreshing all registered queries (${this.queryRegistry.size} tables)`);
    for (const [tableName, fns] of this.queryRegistry) {
      for (const fn of fns) {
        try {
          await fn();
        } catch (e) {
          this.log.warn(`[DB] refreshAllQueries: error refreshing query for ${tableName}`, e);
        }
      }
    }
  }

  /**
   * Register a query refresh function for a table
   * Returns unregister function
   */
  private registerQueryForTable(tableName: string, refreshFn: QueryRefreshFn): () => void {
    if (!this.queryRegistry.has(tableName)) {
      this.queryRegistry.set(tableName, new Set());
    }
    this.queryRegistry.get(tableName)!.add(refreshFn);
    this.log.debug(`[DB] 📝 Registered query for table: ${tableName}`);

    // Return unregister function
    return () => {
      const queries = this.queryRegistry.get(tableName);
      if (queries) {
        queries.delete(refreshFn);
        this.log.debug(`[DB] 🗑️ Unregistered query for table: ${tableName}`);
        if (queries.size === 0) {
          this.queryRegistry.delete(tableName);
        }
      }
    };
  }

  /**
   * Create a reactive query that automatically refreshes when table data changes
   *
   * @param tableName - The table to monitor for changes
   * @param queryFn - Function that fetches the data
   * @param initialValue - Value exposed before the first refresh resolves (defaults to `[]`,
   *   which is only correct when T is an array type — pass an explicit value otherwise,
   *   e.g. `new Map()` for Map-returning queries)
   * @returns QuerySignal with data, loading, and error signals
   *
   * @example
   * // In a service
   * createFormsQuery() {
   *   return this.db.createQuery('interaction_types', () => this.getAll());
   * }
   */
  createQuery<T>(
    tableName: string,
    queryFn: () => Promise<T>,
    initialValue: T = [] as any,
  ): QuerySignal<T> {
    this.log.debug(`[DB] 📊 Creating reactive query for ${tableName}`);

    // Create writable signals
    const data = signal<T>(initialValue);
    const loading = signal(true);
    const error = signal<string | null>(null);

    // Refresh function — guarded against paused/uninitialized DB state
    const refresh = async () => {
      this.log.debug(`[DB] 🔄 Refreshing ${tableName} query...`);
      try {
        await this.init();
        if (!this.db) {
          // Database not queryable (e.g. paused for leadership transition)
          this.log.debug(`[DB] ⏸ Skipping ${tableName} refresh — database not ready`);
          return;
        }
        const results = await queryFn();
        data.set(results);
        error.set(null);
        this.log.debug(`[DB] ✅ Query refreshed for ${tableName}`);
      } catch (err) {
        this.log.error(`[DB] ❌ Query refresh failed for ${tableName}:`, err);
        error.set(err instanceof Error ? err.message : String(err));
      }
    };

    // Initial load
    refresh().finally(() => loading.set(false));

    // Register for automatic refresh when table changes
    this.registerQueryForTable(tableName, refresh);

    return {
      data: data.asReadonly(),
      loading: loading.asReadonly(),
      error: error.asReadonly(),
    };
  }

  /**
   * Track database changes for sync to server
   * Parses INSERT/UPDATE/DELETE statements and logs to _sync_log
   */
  private async trackChangeForSync(sql: string, params?: any[]): Promise<void> {
    try {
      // Skip tracking for system tables and sync log itself
      const normalizedSql = sql.trim().toUpperCase();
      if (
        normalizedSql.includes('_SYNC_LOG') ||
        normalizedSql.includes('_SCHEMA_VERSION') ||
        normalizedSql.includes('_MIGRATIONS')
      ) {
        return;
      }

      // Extract operation type and table name
      let operation: 'INSERT' | 'UPDATE' | 'DELETE' | null = null;
      let tableName: string | null = null;
      let rowId: string | null = null;

      if (normalizedSql.startsWith('INSERT')) {
        operation = 'INSERT';
        // Match all INSERT variants: INSERT INTO, INSERT OR IGNORE INTO, INSERT OR REPLACE INTO
        const match = normalizedSql.match(/INSERT(?:\s+OR\s+\w+)?\s+INTO\s+(\w+)/);
        tableName = match ? match[1].toLowerCase() : null;
        // For INSERT, row_id is the first param (id column, or hash for form_blobs)
        rowId = params && params.length > 0 ? String(params[0]) : null;
      } else if (normalizedSql.startsWith('UPDATE')) {
        operation = 'UPDATE';
        const match = normalizedSql.match(/UPDATE\s+(\w+)/);
        tableName = match ? match[1].toLowerCase() : null;
        // For UPDATE, row_id is typically the last param (WHERE id = ?)
        rowId = params && params.length > 0 ? String(params[params.length - 1]) : null;
      } else if (normalizedSql.startsWith('DELETE FROM')) {
        operation = 'DELETE';
        const match = normalizedSql.match(/DELETE FROM\s+(\w+)/);
        tableName = match ? match[1].toLowerCase() : null;
        // For DELETE, row_id is the param (WHERE id = ?)
        rowId = params && params.length > 0 ? String(params[0]) : null;
      }

      if (!operation || !tableName || !rowId) {
        // Can't track this query - no clear operation/table/id
        return;
      }

      // Fetch the current row data (for INSERT/UPDATE)
      let rowData: any = null;
      if (operation !== 'DELETE') {
        // form_blobs uses 'hash' as primary key, not 'id'
        const pkCol = tableName === 'form_blobs' ? 'hash' : 'id';
        const rows = await this.db!.execO(`SELECT * FROM ${tableName} WHERE ${pkCol} = ?`, [rowId]);
        if (rows && rows.length > 0) {
          rowData = rows[0];
        }
      }

      // Log to _sync_log
      await this.db!.execO(
        `INSERT INTO _sync_log (table_name, row_id, operation, row_data, timestamp, synced)
         VALUES (?, ?, ?, ?, ?, 0)`,
        [tableName, rowId, operation, rowData ? JSON.stringify(rowData) : null, Date.now()],
      );

      this.log.debug(`[DB] 📝 Tracked ${operation} on ${tableName}.${rowId} for sync`);

      // Trigger sync upload (via global event or service)
      this.notifySyncTrigger(tableName, operation, rowId);
    } catch (error) {
      // Don't fail the operation if sync tracking fails
      this.log.error('[DB] ⚠️ Failed to track change for sync:', error);
    }
  }

  /**
   * Notify that a sync upload should be triggered
   * This is called after tracking changes to trigger upload to server
   */
  private notifySyncTrigger(tableName: string, operation: string, rowId: string): void {
    // Emit event for CustomSyncService to pick up
    window.dispatchEvent(
      new CustomEvent('db-change-tracked', {
        detail: { table: tableName, operation, rowIds: [rowId] },
      }),
    );
  }

  /**
   * Get pending sync changes from _sync_log
   */
  async getPendingSyncChanges(): Promise<any[]> {
    try {
      await this.init();
      const changes = await this.exec(
        'SELECT * FROM _sync_log WHERE synced = 0 ORDER BY timestamp ASC',
      );
      return changes || [];
    } catch (error) {
      this.log.error('[DB] ❌ Failed to get pending sync changes:', error);
      return [];
    }
  }

  /**
   * Mark sync changes as synced
   */
  async markChangesSynced(changeIds: number[]): Promise<void> {
    if (changeIds.length === 0) return;

    try {
      await this.init();
      const placeholders = changeIds.map(() => '?').join(',');
      await this.run(`UPDATE _sync_log SET synced = 1 WHERE id IN (${placeholders})`, changeIds);
      this.log.debug(`[DB] ✅ Marked ${changeIds.length} changes as synced`);
    } catch (error) {
      this.log.error('[DB] ❌ Failed to mark changes as synced:', error);
      throw error;
    }
  }

  /**
   * Generate a unique ID for use as a primary key.
   *
   * Uses nanoid for short, URL-friendly, collision-resistant IDs.
   * Required for offline-first sync — IDs must be globally unique
   * without server coordination.
   *
   * @returns A new unique string ID (nanoid).
   */
  /** Single place that constructs the DB worker (the bundler recognises this exact pattern). */
  private static spawnWorker(): Worker {
    return new Worker(new URL('../workers/database.worker', import.meta.url));
  }

  generateId(): string {
    return nanoid();
  }

  /**
   * Get the site ID for this client instance.
   * Used to identify this client in the sync system.
   */
  getSiteId(): string {
    return this.siteId!;
  }

  /**
   * @internal Get the raw database instance — **for SyncService only**.
   *
   * Exposes the low-level database handle so SyncService can share the same
   * connection. **Do NOT use this in feature services** — use {@link exec},
   * {@link run}, or {@link get} instead, which provide mutex serialization
   * and error handling.
   *
   * @returns The raw DB instance, or null if not initialized.
   */
  async getDbInstance(): Promise<DB | null> {
    await this.init();
    return this.db;
  }

  /**
   * @internal Export database for backup/debugging — **not yet implemented**.
   * @deprecated Stub only. Do not depend on this.
   */
  exportDatabase(): Uint8Array | null {
    // TODO: Implement proper export using wa-sqlite API if needed for debugging
    // For now, database is automatically persisted via OPFS
    this.log.debug('Database export not yet implemented');
    return null;
  }

  /**
   * @internal Import database from backup — **not yet implemented**.
   * @deprecated Stub only. Do not depend on this.
   */
  async importDatabase(_data: Uint8Array): Promise<void> {
    // TODO: Implement if needed
    this.log.debug('Database import not yet implemented');
    throw new Error('Import not implemented');
  }

  /**
   * Seed the asset form definition
   * This will overwrite any existing definition (for now)
   */
  private async seedCheckPointFormDefinition(): Promise<void> {
    if (!this.db) return;

    const formId = 'asset_form';
    const formDefinition = {
      fields: [
        {
          key: 'name',
          type: 'input',
          props: {
            label: this.translateService.translate('checkpoint.formDefinition.nameLabel'),
            placeholder: this.translateService.translate(
              'checkpoint.formDefinition.namePlaceholder',
            ),
            required: true,
          },
        },
        {
          key: 'description',
          type: 'textarea',
          props: {
            label: this.translateService.translate('checkpoint.formDefinition.descriptionLabel'),
            placeholder: this.translateService.translate(
              'checkpoint.formDefinition.descriptionPlaceholder',
            ),
            rows: 4,
          },
        },
        {
          key: 'interaction_type_ids',
          type: 'checkbox-group',
          props: {
            label: this.translateService.translate(
              'checkpoint.formDefinition.interactionTypesLabel',
            ),
            description: this.translateService.translate(
              'checkpoint.formDefinition.interactionTypesDescription',
            ),
            options: [], // Will be populated dynamically
            loading: false,
            emptyMessage: this.translateService.translate(
              'checkpoint.formDefinition.interactionTypesEmpty',
            ),
            multiple: true,
          },
        },
      ],
    };

    // Delete existing definition if any
    await this.runInternal('DELETE FROM form_definitions WHERE id = ?', [formId]);

    // Insert new definition using correct schema columns
    const now = new Date().toISOString();
    await this.runInternal(
      `INSERT INTO form_definitions (id, data, created_by, created_at, updated_by, updated_at)
       VALUES (?, ?, 'system', ?, 'system', ?)`,
      [formId, JSON.stringify(formDefinition), now, now],
    );

    this.log.debug('Asset form definition seeded successfully');
  }

  /**
   * Seed the interaction type form definition
   * This will overwrite any existing definition (for now)
   */
  private async seedFormFormDefinition(): Promise<void> {
    if (!this.db) return;

    const formId = 'interaction_type_form';
    const formDefinition = {
      fields: [
        {
          key: 'name',
          type: 'input',
          props: {
            label: this.translateService.translate('form.form.nameLabel'),
            placeholder: this.translateService.translate('form.form.namePlaceholder'),
            required: true,
          },
        },
        {
          key: 'prefill_last_values',
          type: 'checkbox',
          props: {
            label: this.translateService.translate('form.form.prefillLastValuesLabel'),
            description: this.translateService.translate('form.form.prefillLastValuesDescription'),
          },
        },
      ],
    };

    // Delete existing definition if any
    await this.runInternal('DELETE FROM form_definitions WHERE id = ?', [formId]);

    // Insert new definition using correct schema columns
    const now = new Date().toISOString();
    await this.runInternal(
      `INSERT INTO form_definitions (id, data, created_by, created_at, updated_by, updated_at)
       VALUES (?, ?, 'system', ?, 'system', ?)`,
      [formId, JSON.stringify(formDefinition), now, now],
    );

    this.log.debug('Interaction type form definition seeded successfully');
  }

  /**
   * @deprecated DEAD CODE — was part of the removed vlcn.io/cr-sqlite sync system.
   * The `crsql_changes` virtual table no longer exists. Nobody calls this method.
   * Safe to delete once confirmed no downstream references remain.
   */
  async getLocalChanges(_sinceVersion = 0): Promise<any[]> {
    this.log.warn('getLocalChanges() is dead code from the removed cr-sqlite sync system');
    return [];
  }

  /**
   * @deprecated DEAD CODE — was part of the removed vlcn.io/cr-sqlite sync system.
   * The `crsql_changes` virtual table no longer exists. Nobody calls this method.
   * Safe to delete once confirmed no downstream references remain.
   */
  async applyRemoteChanges(_changes: any[]): Promise<number> {
    this.log.warn('applyRemoteChanges() is dead code from the removed cr-sqlite sync system');
    return 0;
  }

  /**
   * @deprecated DEAD CODE — was part of the removed vlcn.io/cr-sqlite sync system.
   * The `crsql_db_version()` function no longer exists.
   * Only referenced by sync-debug.service.ts (also a stub).
   * Safe to delete once confirmed no downstream references remain.
   */
  async getCurrentVersion(): Promise<number> {
    return 0;
  }

  /**
   * Delete the persisted SQLite database from IndexedDB.
   * Tries the Worker first (if running), falls back to direct IDB deletion.
   * @param dbPath - Path/key of the database (e.g., 'cp2-customer_xyz')
   * @param logPrefix - Prefix for log messages (e.g., '[DB RESET]')
   */
  private async deletePersistedDatabase(dbPath: string, logPrefix: string): Promise<void> {
    // OPFS SAH pool files can only be deleted from a DB worker. Use the running
    // one, or spawn a temporary worker (callers usually terminate theirs first).
    const runningProxy = DatabaseService.globalWorkerProxy;
    const proxy =
      runningProxy ??
      new DatabaseWorkerProxy(DatabaseService.spawnWorker(), this.log.error.bind(this.log));
    try {
      await proxy.send('deleteDatabase', { dbPath });
      this.log.debug(`${logPrefix} ✅ Database deleted via Worker: ${dbPath}`);
      return;
    } catch {
      this.log.warn(`${logPrefix} ⚠️ Worker delete failed, falling back to direct IDB deletion`);
    } finally {
      if (!runningProxy) proxy.terminate();
    }

    // Direct IndexedDB deletion (for when Worker is not available)
    this.log.debug(`${logPrefix} Deleting database from IndexedDB: ${dbPath}`);
    await this.deleteFromIDBDirect(dbPath);
    this.log.debug(`${logPrefix} ✅ Database deleted from IndexedDB: ${dbPath}`);
  }

  /**
   * Delete a database entry directly from IndexedDB (no Worker needed).
   * Used as a fallback when the Worker is not running.
   */
  private deleteFromIDBDirect(dbPath: string): Promise<void> {
    const IDB_NAME = 'checkpoint-sqljs';
    const IDB_STORE = 'databases';
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(IDB_STORE)) {
          req.result.createObjectStore(IDB_STORE);
        }
      };
      req.onsuccess = () => {
        const idb = req.result;
        const tx = idb.transaction(IDB_STORE, 'readwrite');
        const store = tx.objectStore(IDB_STORE);
        store.delete(dbPath);
        tx.oncomplete = () => {
          idb.close();
          resolve();
        };
        tx.onerror = () => {
          idb.close();
          reject(tx.error);
        };
      };
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Emergency cleanup utility - deletes ALL wa-sqlite IndexedDB databases
   * ⚠️ WARNING: This is a nuclear option for development/testing only!
   * Use deletePersistedDatabase() for normal operations.
   * @param logPrefix - Prefix for log messages
   */
  private async emergencyCleanupAllDatabases(logPrefix = '[EMERGENCY]'): Promise<void> {
    this.log.warn(`${logPrefix} ⚠️ Performing emergency cleanup of ALL wa-sqlite databases`);

    const idbDatabases = await indexedDB.databases();
    const waDbNames = idbDatabases
      .filter((db) => db.name && (db.name.includes('wa-sqlite') || db.name.includes('checkpoint')))
      .map((db) => db.name as string);

    this.log.info(
      `${logPrefix} Found ${waDbNames.length} IndexedDB databases to delete:`,
      waDbNames,
    );

    for (const idbName of waDbNames) {
      await new Promise<void>((resolve, reject) => {
        const request = indexedDB.deleteDatabase(idbName);

        request.onsuccess = () => {
          this.log.debug(`${logPrefix} ✅ Deleted IndexedDB: ${idbName}`);
          resolve();
        };

        request.onerror = () => {
          this.log.error(`${logPrefix} ❌ Failed to delete IndexedDB: ${idbName}`, request.error);
          reject(request.error);
        };

        request.onblocked = () => {
          this.log.warn(
            `${logPrefix} ⚠️ Database deletion blocked (database still open): ${idbName}`,
          );
          // Continue anyway - database will be deleted when connections close
          setTimeout(() => resolve(), 100);
        };
      });
    }
  }

  /**
   * Public API for emergency cleanup - FOR DEVELOPMENT/TESTING ONLY
   * Deletes ALL wa-sqlite databases and resets all state.
   * This is a nuclear option that should only be exposed in dev/test UIs.
   */
  async nuclearReset(): Promise<void> {
    this.log.warn('[NUCLEAR RESET] 💥 Starting complete database reset...');

    // Close any open connections
    if (this.db) {
      try {
        await this.db.close();
      } catch {
        // Ignore close errors
      }
    }

    // Clear all in-memory state
    this.db = null;
    this.initPromise = null;
    this.initState = 'uninitialized';
    this.initError = null;
    DatabaseService.globalDb = null;
    this.queryRegistry.clear();

    // Terminate Worker if running
    if (DatabaseService.globalWorkerProxy) {
      DatabaseService.globalWorkerProxy.terminate();
      DatabaseService.globalWorkerProxy = null;
    }
    DatabaseService.globalWorker = null;

    // Delete all IndexedDB databases
    await this.emergencyCleanupAllDatabases('[NUCLEAR RESET]');

    this.log.warn('[NUCLEAR RESET] ✅ Complete reset finished - all databases deleted');
  }

  /**
   * Reset database after backend sync generation change
   * Deletes only the customer-specific IndexedDB database
   * Preserves auth tokens and preferences in sessionStorage/localStorage
   */
  async resetDatabase(): Promise<void> {
    this.log.info('🔄 Resetting local database due to backend sync...');

    // Close current connection
    if (this.db) {
      this.log.debug('Closing current database connection...');
      this.db.close();
      this.db = null;
    }

    // Terminate Worker and reset state
    if (DatabaseService.globalWorkerProxy) {
      DatabaseService.globalWorkerProxy.terminate();
      DatabaseService.globalWorkerProxy = null;
    }
    DatabaseService.globalWorker = null;
    DatabaseService.globalDb = null;
    this.initPromise = null;
    this.initState = 'uninitialized';
    this.initError = null;

    // Delete database (customer-specific only)
    const dbPath = this.getDbName();
    this.log.info(`Deleting database: ${dbPath}`);

    await this.deletePersistedDatabase(dbPath, '[DB SYNC]');

    this.log.info('✅ Database reset complete - ready for fresh sync');
  }

  /**
   * Clear all data (for testing/reset)
   * Deletes the IndexedDB database completely
   */
  async clearDatabase(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    if (DatabaseService.globalWorkerProxy) {
      DatabaseService.globalWorkerProxy.terminate();
      DatabaseService.globalWorkerProxy = null;
    }
    DatabaseService.globalWorker = null;
    DatabaseService.globalDb = null;
    this.initPromise = null;
    this.initState = 'uninitialized';
    this.initError = null;

    // Delete the database
    const dbPath = this.getDbName();
    await this.deletePersistedDatabase(dbPath, '[DB CLEAR]');
    this.log.debug('Database cleared successfully');
  }
}
